import { useEffect, useRef, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  api,
  type DialpadActive,
  type DialpadCoverage,
  type DialpadSettings,
  type DialpadSyncWindow,
  type UserOut,
} from "../lib/api";
import {
  fmtDurationMs,
  fmtRange,
  fmtSeconds,
} from "../lib/format";
import { activePollMs } from "../lib/polling";
import {
  DialpadCoverageTile,
} from "./dialpad/DialpadCoverageTile";
import { DialpadReconciliationTile } from "./dialpad/DialpadReconciliationTile";
import { DialpadSyncScheduleTile } from "./dialpad/DialpadSyncScheduleTile";
import { SyncHistorySection } from "./dialpad/SyncHistorySection";

function isAssessmentRunTerminal(status: string | undefined): boolean {
  return (
    status === "completed" ||
    status === "failed" ||
    status === "partial" ||
    status === "cancelled"
  );
}

export function DialpadView({ me }: { me: UserOut }) {
  const qc = useQueryClient();
  // Ticks once a second so the Phase 0 "running" elapsed timer stays live
  // without us having to refetch the active-sync poll any faster.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const activeQ = useQuery<DialpadActive>({
    queryKey: ["dialpad-active"],
    queryFn: () => api.dialpadActive(),
    refetchInterval: (q) => activePollMs(q.state.data?.has_active),
    placeholderData: keepPreviousData,
  });

  const windowsQ = useQuery<DialpadSyncWindow[]>({
    queryKey: ["dialpad-windows"],
    queryFn: () => api.dialpadWindows(),
    refetchInterval: (q) => {
      const hasInFlight = (q.state.data ?? []).some((w) =>
        w.status === "pending" || w.status === "running"
      );
      return hasInFlight ? 3000 : 15000;
    },
    placeholderData: keepPreviousData,
  });
  const [confirmingClear, setConfirmingClear] = useState(false);
  const clearM = useMutation({
    mutationFn: () => api.dialpadClearData(),
    onSuccess: () => {
      setConfirmingClear(false);
      qc.invalidateQueries({ queryKey: ["dialpad-windows"] });
      qc.invalidateQueries({ queryKey: ["dialpad-active"] });
      qc.invalidateQueries({ queryKey: ["dialpad-coverage"] });
    },
  });
  const clearErr = clearM.error as { message?: string } | null;

  // Manual retry for viable calls missing a transcript row (admin-gated).
  // Phase 2 of the window sync will pick these up next sync, but this
  // gives admins a button to chase them down on demand from the
  // coverage tile.
  const retryTranscriptsM = useMutation({
    mutationFn: () => api.dialpadRetryMissingTranscripts(),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["dialpad-retry-missing-transcripts-active"],
      });
    },
  });
  const retryTranscriptsErr = retryTranscriptsM.error as
    | { message?: string }
    | null;
  const retryTranscriptsActiveQ = useQuery({
    queryKey: ["dialpad-retry-missing-transcripts-active"],
    queryFn: () => api.dialpadRetryMissingTranscriptsActive(),
    refetchInterval: (q) => activePollMs(q.state.data?.has_active),
    placeholderData: keepPreviousData,
  });
  const retryTranscriptsRunning = !!retryTranscriptsActiveQ.data?.has_active;
  const prevRetryTranscriptsRunningRef = useRef(false);
  useEffect(() => {
    if (prevRetryTranscriptsRunningRef.current && !retryTranscriptsRunning) {
      qc.invalidateQueries({ queryKey: ["dialpad-coverage"] });
      retryTranscriptsM.reset();
    }
    prevRetryTranscriptsRunningRef.current = retryTranscriptsRunning;
  }, [retryTranscriptsRunning, qc, retryTranscriptsM]);

  // Quick "sync from last" trigger driven by the schedule tile. The
  // heavier Advanced Sync Call Data controls live on the Data view; the
  // in-progress panel and run history below read the same shared server
  // state, so progress and history still surface here.
  const quickIngestM = useMutation({
    mutationFn: async (w: { start: string; end: string }) =>
      api.dialpadIngest(w.start, w.end, false),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dialpad-active"] });
      qc.invalidateQueries({ queryKey: ["dialpad-windows"] });
      qc.invalidateQueries({ queryKey: ["dialpad-coverage"] });
      qc.invalidateQueries({ queryKey: ["dialpad-users"] });
    },
  });
  const quickIngestErr = quickIngestM.error as { message?: string } | null;

  const coverageQ = useQuery<DialpadCoverage>({
    queryKey: ["dialpad-coverage"],
    queryFn: () => api.dialpadCoverage(),
    placeholderData: keepPreviousData,
  });
  // Re-fetch coverage when an ingest/backfill transitions from running to
  // idle so numbers reflect the freshly-ingested data.
  const prevHasActiveRef = useRef(false);
  useEffect(() => {
    const running = !!activeQ.data?.has_active;
    if (prevHasActiveRef.current && !running) {
      qc.invalidateQueries({ queryKey: ["dialpad-coverage"] });
      qc.invalidateQueries({ queryKey: ["dialpad-users"] });
      qc.invalidateQueries({ queryKey: ["dialpad-windows"] });
    }
    prevHasActiveRef.current = running;
  }, [activeQ.data?.has_active, qc]);

  // Task #382 — Dialpad settings include the supadmin-only
  // ``sync_buttons_disabled`` flag that gates the "From last sync" and
  // "Add schedule" buttons on the DialpadSyncScheduleTile. Loaded here
  // so the tile can render the disabled state and the supadmin checkbox.
  const dialpadSettingsQ = useQuery<DialpadSettings>({
    queryKey: ["dialpad-settings"],
    queryFn: () => api.dialpadSettings(),
    enabled: me.role === "admin" || me.role === "supadmin",
  });
  const setSyncButtonsDisabledM = useMutation({
    mutationFn: (v: boolean) =>
      api.setDialpadSyncButtonsDisabled(v),
    onMutate: async (v: boolean) => {
      await qc.cancelQueries({ queryKey: ["dialpad-settings"] });
      const prev = qc.getQueryData<DialpadSettings>(["dialpad-settings"]);
      if (prev) {
        qc.setQueryData<DialpadSettings>(["dialpad-settings"], {
          ...prev,
          sync_buttons_disabled: v,
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["dialpad-settings"], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["dialpad-settings"] });
    },
  });
  const setSyncButtonsDisabledErr = setSyncButtonsDisabledM.error as
    | { message?: string }
    | null;

  // ---------- Ticket 8 — Phase 3 (LLM-graded assessment runs) ----------
  const assessmentRunActiveQ = useQuery({
    queryKey: ["assessment-run-active"],
    queryFn: () => api.assessmentRunsActive(),
    refetchInterval: (q) => activePollMs(q.state.data?.has_active),
    placeholderData: keepPreviousData,
    enabled: me.role === "supadmin" || me.role === "admin",
  });
  const assessmentRunRunning = !!assessmentRunActiveQ.data?.has_active;

  const retryFailedAssessmentsM = useMutation({
    mutationFn: () => api.dialpadRetryFailedAssessments(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assessment-run-active"] });
    },
  });
  const retryFailedAssessmentsErr = retryFailedAssessmentsM.error as
    | { message?: string }
    | null;
  const prevAssessmentRunRunningRef = useRef(false);
  useEffect(() => {
    if (prevAssessmentRunRunningRef.current && !assessmentRunRunning) {
      qc.invalidateQueries({ queryKey: ["dialpad-coverage"] });
      retryFailedAssessmentsM.reset();
    }
    prevAssessmentRunRunningRef.current = assessmentRunRunning;
  }, [assessmentRunRunning, qc, retryFailedAssessmentsM]);

  // Poll the linked Phase 3 job for the currently-active sync window so
  // the third tile in the active-sync card can render its progress.
  const activeSyncWindowId = activeQ.data?.sync_window_id ?? null;
  const assessmentRunByWindowQ = useQuery({
    queryKey: ["assessment-run-by-window", activeSyncWindowId],
    queryFn: () => api.assessmentRunByWindow(activeSyncWindowId!),
    refetchInterval: (q) => {
      if (!activeQ.data?.has_active) return false;
      const s = q.state.data?.status;
      return isAssessmentRunTerminal(s) ? false : 2000;
    },
    placeholderData: keepPreviousData,
    // Task #271: was supadmin-only; now any role that can reach this
    // page must be able to see Phase 3 progress on the IN PROGRESS card.
    enabled: !!activeSyncWindowId,
  });
  // For a multi-window batch the live card aggregates every sibling, so the
  // Phase 3 tile must show the batch-wide assessment_run (agg_ar, summed
  // across all windows) — not the lead window's by-window query, which
  // under-reports and resets when the lead rolls over to the next sibling.
  // For a single-window run the by-window query is fresher (polls every 2s),
  // so prefer it there and fall back to the embedded snapshot (task #271).
  const isBatch = (activeQ.data?.total_windows ?? 1) > 1;
  const assessmentRunForWindow = isBatch
    ? (activeQ.data?.assessment_run ?? assessmentRunByWindowQ.data ?? null)
    : (assessmentRunByWindowQ.data ?? activeQ.data?.assessment_run ?? null);

  const hasActive = activeQ.data?.has_active;

  return (
    <div className="space-y-6">
      <div>
        <h2
          className="text-2xl font-semibold text-foreground"
          style={{ fontFamily: "var(--app-font-serif)" }}
        >
          Sync to Dialpad and Assess
        </h2>
      </div>
      <div className="space-y-6">
        {(me.role === "admin" || me.role === "supadmin") && (
          <DialpadSyncScheduleTile
            me={me}
            lastCallAt={coverageQ.data?.date_coverage.last_call_at ?? null}
            hasActive={!!hasActive}
            isQueuing={quickIngestM.isPending}
            onSyncFromLast={(w) => quickIngestM.mutate(w)}
            ingestError={quickIngestErr?.message ?? null}
            syncButtonsDisabled={
              dialpadSettingsQ.data?.sync_buttons_disabled ?? false
            }
            onToggleSyncButtonsDisabled={(v) =>
              setSyncButtonsDisabledM.mutate(v)
            }
            toggleError={setSyncButtonsDisabledErr?.message ?? null}
          />
        )}
      </div>

      {hasActive && activeQ.data && (
        <div className="rounded-lg border border-secondary/30 bg-secondary/10 p-4 shadow-md">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wide text-secondary">
                In progress
              </div>
              <div className="font-medium text-foreground mt-1">
                {activeQ.data.window_start && activeQ.data.window_end
                  ? fmtRange(activeQ.data.window_start, activeQ.data.window_end)
                  : "Working…"}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Status: {activeQ.data.job_status || "queued"}
                {activeQ.data.min_duration_ms != null && (
                  <>
                    {" · "}
                    threshold {fmtSeconds(activeQ.data.min_duration_ms / 1000)}
                  </>
                )}
                {activeQ.data.phases &&
                  activeQ.data.phases !== "all" && (
                    <>
                      {" · "}
                      <span className="font-medium text-foreground">
                        {activeQ.data.phases === "phase1"
                          ? "Phase 1 only"
                          : activeQ.data.phases === "phase2"
                          ? "Phase 2 only"
                          : "Phase 3 only"}
                      </span>
                    </>
                  )}
                {activeQ.data.total_windows != null &&
                  activeQ.data.total_windows > 1 && (
                    <>
                      {" · "}
                      {activeQ.data.total_windows} day-windows in parallel
                      {" · "}
                      {activeQ.data.completed_windows ?? 0} of{" "}
                      {activeQ.data.total_windows} done
                    </>
                  )}
              </div>
              {(() => {
                const ug = activeQ.data.users_groups_status;
                if (!ug) return null;
                const running =
                  ug === "running" || ug === "pending" || activeQ.data.phase === "users_groups";
                const succeeded = ug === "succeeded";
                const failed = ug === "failed";
                const u = activeQ.data.users_upserted ?? 0;
                const g = activeQ.data.groups_upserted ?? 0;
                const startedAt = activeQ.data.users_groups_started_at;
                const finishedAt = activeQ.data.users_groups_finished_at;
                let elapsedLabel: string | null = null;
                if (startedAt) {
                  const startMs = new Date(startedAt).getTime();
                  if (Number.isFinite(startMs)) {
                    const endMs =
                      !running && finishedAt
                        ? new Date(finishedAt).getTime()
                        : nowTick;
                    const diff = Math.max(0, endMs - startMs);
                    elapsedLabel = fmtDurationMs(diff);
                  }
                }
                return (
                  <div
                    className={`mt-3 rounded border p-2 text-sm ${
                      failed
                        ? "border-destructive/40 bg-destructive/5"
                        : running
                        ? "border-primary bg-card"
                        : "border-border bg-card/60"
                    }`}
                  >
                    <div className="text-xs font-medium text-muted-foreground">
                      Phase 0 — Users &amp; Groups{" "}
                      {running && (
                        <span className="text-secondary">(running)</span>
                      )}
                      {succeeded && (
                        <span className="text-primary">(done)</span>
                      )}
                      {failed && (
                        <span className="text-destructive">(failed)</span>
                      )}
                      {elapsedLabel && (
                        <span className="ml-1 text-muted-foreground">
                          · {elapsedLabel}
                          {running && " elapsed"}
                        </span>
                      )}
                    </div>
                    <div className="text-foreground mt-0.5 text-xs">
                      {failed ? (
                        <span className="text-destructive">
                          {activeQ.data.users_groups_error ||
                            "Users/groups sync failed."}
                        </span>
                      ) : running ? (
                        <span className="text-muted-foreground">
                          Syncing users &amp; groups…
                        </span>
                      ) : (
                        <span className="text-muted-foreground">
                          <span className="text-foreground font-medium">
                            {u}
                          </span>{" "}
                          users ·{" "}
                          <span className="text-foreground font-medium">
                            {g}
                          </span>{" "}
                          groups upserted
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
              {(() => {
                // Drive each phase box from the per-phase status the backend
                // aggregates across all batch windows. Because those statuses
                // are monotonic (an earlier phase is never less advanced than
                // a later one), the boxes can no longer show e.g. Phase 3
                // "done" while Phase 2 still reads "waiting".
                const cs = activeQ.data.calls_status;
                const ts = activeQ.data.transcripts_status;
                const as_ = activeQ.data.assessments_status;
                const arw = assessmentRunForWindow;
                // For a multi-day batch, each phase box shows how many of the
                // N day-windows have FINISHED that phase ("K/N done"), so the
                // parallel per-day progress reads honestly instead of a bare
                // "partial". "done" (not "windows") so it can't be misread as
                // "K windows are in this phase". Hidden for a single-window run.
                const totalW = activeQ.data.total_windows ?? 0;
                const windowsDone = (done: number | null | undefined) =>
                  isBatch && done != null ? (
                    <span className="text-muted-foreground">
                      {" · "}
                      {done}/{totalW} done
                    </span>
                  ) : null;
                return (
                  <div className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
                    <div
                      className={`rounded border p-2 ${
                        cs === "running"
                          ? "border-primary bg-card"
                          : "border-border bg-card/60"
                      }`}
                    >
                      <div className="text-xs font-medium text-muted-foreground">
                        Phase 1 — Calls{" "}
                        {cs === "running" && (
                          <span className="text-secondary">(running)</span>
                        )}
                        {cs === "partial" && (
                          <span className="text-secondary">(partial)</span>
                        )}
                        {cs === "done" && (
                          <span className="text-primary">(done)</span>
                        )}
                        {windowsDone(activeQ.data.calls_windows_done)}
                      </div>
                      <div className="text-foreground mt-0.5">
                        <span className="font-medium">
                          {activeQ.data.calls_ingested ?? 0}
                        </span>
                        <span className="text-muted-foreground">
                          {" "}
                          / {activeQ.data.calls_found ?? 0}
                        </span>{" "}
                        <span className="text-xs text-muted-foreground">stored</span>
                      </div>
                    </div>
                    <div
                      className={`rounded border p-2 ${
                        ts === "running"
                          ? "border-primary bg-card"
                          : "border-border bg-card/60"
                      }`}
                    >
                      <div className="text-xs font-medium text-muted-foreground">
                        Phase 2 — Transcripts{" "}
                        {ts === "running" && (
                          <span className="text-secondary">(running)</span>
                        )}
                        {ts === "partial" && (
                          <span className="text-secondary">(partial)</span>
                        )}
                        {ts === "done" && (
                          <span className="text-primary">(done)</span>
                        )}
                        {windowsDone(activeQ.data.transcripts_windows_done)}
                      </div>
                      {ts === "waiting" ? (
                        <div className="text-foreground mt-0.5">
                          <span className="text-xs text-muted-foreground">
                            waiting
                          </span>
                        </div>
                      ) : (
                        <div className="text-foreground mt-0.5 space-y-0.5">
                          <div>
                            <span className="font-medium">
                              {activeQ.data.transcripts_ingested ?? 0}
                            </span>{" "}
                            <span className="text-xs text-muted-foreground">
                              fetched
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            skipped — too short:{" "}
                            <span className="text-foreground">
                              {activeQ.data.transcripts_skipped_short ?? 0}
                            </span>
                            , no duration:{" "}
                            <span className="text-foreground">
                              {activeQ.data.transcripts_skipped_no_duration ?? 0}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div
                      className={`rounded border p-2 ${
                        as_ === "running"
                          ? "border-primary bg-card"
                          : "border-border bg-card/60"
                      }`}
                    >
                      <div className="text-xs font-medium text-muted-foreground">
                        Phase 3 — Assessments{" "}
                        {as_ === "running" && (
                          <span className="text-secondary">(running)</span>
                        )}
                        {as_ === "partial" && (
                          <span className="text-secondary">(partial)</span>
                        )}
                        {as_ === "done" &&
                          arw?.status !== "failed" &&
                          arw?.status !== "partial" && (
                            <span className="text-primary">(done)</span>
                          )}
                        {as_ === "done" && arw?.status === "partial" && (
                          <span className="text-amber-600">(partial)</span>
                        )}
                        {arw?.status === "failed" && (
                          <span className="text-destructive">(failed)</span>
                        )}
                        {windowsDone(activeQ.data.assessments_windows_done)}
                      </div>
                      <div className="text-foreground mt-0.5">
                        {as_ === "waiting" ? (
                          <span className="text-xs text-muted-foreground">
                            waiting
                          </span>
                        ) : arw ? (
                          <>
                            <span className="font-medium">
                              {arw.progress.completed}
                            </span>
                            <span className="text-muted-foreground">
                              {" "}/ {arw.progress.total}
                            </span>{" "}
                            <span className="text-xs text-muted-foreground">
                              pairs
                            </span>
                            {arw.progress.failed > 0 && (
                              <span className="text-xs text-muted-foreground">
                                {" "}·{" "}
                                <span className="text-destructive">
                                  {arw.progress.failed} failed
                                </span>
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            waiting
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="text-right text-sm">
              <div>
                <span className="text-muted-foreground">Errors:</span>{" "}
                <span
                  className={`font-medium ${
                    (activeQ.data.errors ?? 0) > 0 ? "text-destructive" : ""
                  }`}
                >
                  {activeQ.data.errors ?? 0}
                </span>
              </div>
              {activeQ.data.updated_at && (
                <div className="text-xs text-muted-foreground mt-1">
                  updated {new Date(activeQ.data.updated_at).toLocaleTimeString()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <DialpadCoverageTile
        coverage={coverageQ.data}
        isLoading={coverageQ.isLoading}
        isFetching={coverageQ.isFetching}
        onRefresh={() => coverageQ.refetch()}
        retryTranscripts={
          me.role === "supadmin" || me.role === "admin"
            ? {
                onRetry: () => retryTranscriptsM.mutate(),
                isPending: retryTranscriptsM.isPending,
                isRunning: retryTranscriptsRunning,
                active: retryTranscriptsActiveQ.data,
                errorMessage: retryTranscriptsErr?.message ?? null,
              }
            : null
        }
        retryAssessments={
          me.role === "supadmin" || me.role === "admin"
            ? {
                onRetry: () => retryFailedAssessmentsM.mutate(),
                isPending: retryFailedAssessmentsM.isPending,
                isRunning: assessmentRunRunning,
                errorMessage: retryFailedAssessmentsErr?.message ?? null,
              }
            : null
        }
      />

      {(me.role === "supadmin" || me.role === "admin") && (
        <DialpadReconciliationTile role={me.role} />
      )}

      <SyncHistorySection
        windowsQ={windowsQ}
        hasActive={hasActive}
        clearErr={clearErr}
        confirmingClear={confirmingClear}
        setConfirmingClear={setConfirmingClear}
        clearM={clearM}
        role={me.role}
        nowTick={nowTick}
      />
    </div>
  );
}
